/* eslint-disable @atlassian/tangerine/import/no-parent-imports */
/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { useMemo } from 'react';

import { jsx } from '@compiled/react';
import { useIntl } from 'react-intl-next';

import JiraIcon from '@atlaskit/icon/core/ai-chat';
import SearchIcon from '@atlaskit/icon/core/search';

import { useAppContext } from '../../../../common/ui/app-context';
import { FlipList } from '../../../../common/ui/flip-list';
import { SearchResult } from '../../../../common/ui/quick-find/search-dialog-result';
import { getWorkItemPageUrl } from '../../../../common/utils/quick-find/jira-work-item-navigation';
import { useSelectedFilters } from '../../../../controllers/filters/selected';
import { ISSUE_NAVIGATOR_URL } from '../search-page-link/utils';

import { detectIntentMock } from './intent-detection';
import { messages } from './messages';
import type { IntentMode } from './types';
import { useIntentRouting } from './use-intent-routing';
import { useIntentRoutingDisabled } from './use-intent-routing-disabled';

/**
 * Static IntentRouter rows.
 *
 * Returns two destination rows using SearchResult so they integrate
 * with @atlassian/search-dialog keyboard navigation.
 *
 */
export const IntentRouter = ({ query }: { query: string }): React.JSX.Element => {
	const intl = useIntl();
	const { generateSearchUrl, queryParams } = useAppContext();
	const {
		selectedFilters: { jiraFilters },
	} = useSelectedFilters({});

	const allAppsUrl = useMemo(
		() =>
			generateSearchUrl({
				...queryParams,
				text: query,
			}),
		[generateSearchUrl, queryParams, query],
	);

	const jiraWorkItemsUrl = useMemo(() => {
		try {
			return getWorkItemPageUrl({
				query,
				jiraFilters,
				generateSearchUrl,
				queryParams,
			});
		} catch {
			return ISSUE_NAVIGATOR_URL;
		}
	}, [generateSearchUrl, jiraFilters, query, queryParams]);

	const allAppsLabel = intl.formatMessage(messages.allApps);
	const jiraWorkItemsLabel = intl.formatMessage(messages.jiraWorkItems);

	const { disabled, initialPrimaryMode } = useIntentRoutingDisabled();
	// TODO: wire up intentDetection from @atlassian/search-client
	// const intentDetection = useIntentDetectionService();

	const { primaryMode, pause, resume, onKeyboardHighlight } = useIntentRouting({
		query,
		debounceMs: 250,
		intentDetection: detectIntentMock,
		disabled,
		initialPrimaryMode,
	});

	const orderedModes: IntentMode[] =
		primaryMode === 'JIRA_WORK_ITEMS'
			? ['JIRA_WORK_ITEMS', 'ALL_APPS']
			: ['ALL_APPS', 'JIRA_WORK_ITEMS'];

	const rowsByMode: Record<IntentMode, React.JSX.Element> = {
		ALL_APPS: (
			<SearchResult
				key="intent-all-apps"
				id="intent-all-apps"
				resultType="intentRouter"
				icon={<SearchIcon label={allAppsLabel} />}
				title={allAppsLabel}
				url={allAppsUrl}
				onResultHighlighted={onKeyboardHighlight}
			/>
		),
		JIRA_WORK_ITEMS: (
			<SearchResult
				key="intent-jira-work-items"
				id="intent-jira-work-items"
				resultType="intentRouter"
				icon={<JiraIcon label={jiraWorkItemsLabel} />}
				title={jiraWorkItemsLabel}
				url={jiraWorkItemsUrl}
				onResultHighlighted={onKeyboardHighlight}
			/>
		),
	};

	const order = orderedModes.map((mode) => (mode === 'ALL_APPS' ? 'intent-all-apps' : 'intent-jira-work-items'));

	return (
		<FlipList
			order={order}
			disabled={disabled}
			containerAs="div"
			itemAs="div"
			getItemProps={() => ({
				onMouseEnter: pause,
				onMouseLeave: resume,
				onFocusCapture: pause,
				onBlurCapture: resume,
			})}
			renderItem={(key) => {
				return key === 'intent-all-apps' ? rowsByMode.ALL_APPS : rowsByMode.JIRA_WORK_ITEMS;
			}}
		/>
	);
};
